import React from 'react'
import styled from 'styled-components'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { RAG, scoreBandFor } from 'features/dashboard/charts/scoreColour'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { useComplianceSummary } from 'features/compliance/useComplianceSummary'

const plural = (n, one, many) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`

export const complianceChipText = summary => {
  const score = summary?.score
  const scored = score !== null && score !== undefined
  const band = scored ? scoreBandFor(score) : null
  const overdueTasks = Number(summary?.tasks?.overdue) || 0
  const openRemedials = Number(summary?.remedials?.open) || 0
  return {
    scored,
    level: band?.level || 'none',
    scoreText: scored ? `${Math.round(score)}%` : 'Not scored yet',
    bandText: band?.label || null,
    overdueText: plural(overdueTasks, 'overdue task', 'overdue tasks'),
    remedialText: plural(openRemedials, 'open remedial', 'open remedials'),
    overdueTasks,
    openRemedials,
  }
}

const ComplianceChip = ({ scope, onOpen, openLabel = 'Open compliance' }) => {
  const summary = useComplianceSummary(scope)
  if (!summary) return <ChipSkeleton aria-hidden='true' data-testid='compliance-chip-skeleton' />
  const text = complianceChipText(summary)
  const tone = RAG[text.level] || RAG.none
  return (
    <ChipRow>
      <ChipButton
        type='button'
        onClick={onOpen}
        aria-label={`Compliance ${text.scoreText}${text.bandText ? `, ${text.bandText}` : ''}. ${text.overdueText}. ${text.remedialText}. ${openLabel}`}
        title={openLabel}
      >
        <Score $fg={tone.fg} $bg={tone.bg}>
          <span>Compliance {text.scoreText}</span>
          {text.bandText && <BandText>({text.bandText})</BandText>}
        </Score>
        <Fact $alert={text.overdueTasks > 0}>{text.overdueText}</Fact>
        <Fact $alert={text.openRemedials > 0}>{text.remedialText}</Fact>
        <Chevron aria-hidden='true'><ChevronRightRoundedIcon style={{ fontSize: 18 }} /></Chevron>
      </ChipButton>
      <ExplainerSlot><ComplianceScoreExplainer align='left' /></ExplainerSlot>
    </ChipRow>
  )
}

export default ComplianceChip

const ChipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`

const ExplainerSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  white-space: nowrap;
`

const Chevron = styled.span`
  display: inline-flex;
  margin-left: auto;
  color: #64748b;

  @media (max-width: 480px) {
    display: none;
  }
`

const ChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  min-width: 0;
  max-width: 100%;
  padding: 4px 10px 4px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #ffffff;
  font-size: 14px;
  color: #334155;
  cursor: pointer;
  flex-wrap: wrap;
  text-align: left;

  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }

  @media (max-width: 480px) {
    border-radius: 12px;
    padding: 6px 10px;
  }
`

const Score = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 700;
  color: ${({ $fg }) => $fg};
  background: ${({ $bg }) => $bg};
  white-space: nowrap;
`

const BandText = styled.span`
  font-weight: 600;
`

const Fact = styled.span`
  white-space: nowrap;
  font-weight: ${({ $alert }) => ($alert ? 700 : 500)};
  color: ${({ $alert }) => ($alert ? RAG.critical.fg : '#475569')};
`

const ChipSkeleton = styled.span`
  display: inline-block;
  width: 280px;
  max-width: 100%;
  height: 40px;
  border-radius: 999px;
  background: #f1f5f9;
  animation: chipPulse 1.4s ease-in-out infinite;

  @keyframes chipPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.55; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
